import {
  HUBSPOT_FORM_PRIVATE_APP_TOKEN,
  HUBSPOT_URL_BASE,
  MARKETING_FORM_URL,
  SIGNUP_FORM_ID,
} from '@utils/form/hubspot/constants'
import {
  TDependentFieldSpec,
  THubspotFieldType,
  THubspotFormSpecification,
} from './schema'
import {
  TFormFieldType,
  TFormItemSpecification,
  TFormListInputSpecification,
  TFormSpecification,
  TConsentOptions,
} from '../schema'

export async function getFormSpecification(
  formId: string
): Promise<THubspotFormSpecification> {
  const formSpecification = await fetch(
    `${HUBSPOT_URL_BASE}${MARKETING_FORM_URL}/${formId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${HUBSPOT_FORM_PRIVATE_APP_TOKEN}`,
      },
      next: {
        revalidate: 1800, // 30 minutes
      },
    }
  )

  const formSpecificationResponse = await formSpecification.json()

  return formSpecificationResponse as THubspotFormSpecification
}

function toFormComponentType(fieldType: THubspotFieldType): TFormFieldType {
  const hubspotFormTypeMapper: Record<THubspotFieldType, TFormFieldType> = {
    radio: 'RadioTag',
    dropdown: 'Dropdown',
    email: 'EmailInput',
    phone: 'PhoneInput',
    single_line_text: 'Input',
    // don't support on profile page it for now
    single_checkbox: null,
  }

  return hubspotFormTypeMapper[fieldType] ?? 'unknown'
}

export function removeHtmlTags(richText: string) {
  return richText.replace(/(<([^>]+)>)/gi, '')
}
const parseHubspotDependentFields = (
  fields: TDependentFieldSpec[]
): TFormItemSpecification[] => {
  if (!fields?.length) {
    return null
  }

  const dependents = fields.map((field: TDependentFieldSpec) => {
    const { dependentCondition, dependentField } = field
    return {
      condition: {
        operator: dependentCondition?.operator || null,
        values: dependentCondition?.values || null,
      },
      name: dependentField.name,
      required: dependentField.required,
      labelOrQuestion: dependentField.label || '',
      placeholder: dependentField.placeholder || '',
      type: toFormComponentType(dependentField.fieldType),
      values:
        dependentField.options?.map((option) => {
          return {
            key: option.value || null,
            displayLabel: option.label || null,
          }
        }) || null,
    }
  })

  return dependents || null
}

export function convertFormSpecificationToQuestions(
  formSpecification: THubspotFormSpecification
): TFormItemSpecification[] {
  const questions: TFormItemSpecification[] = []

  for (const fieldGroup of formSpecification?.fieldGroups) {
    const { richText, fields } = fieldGroup
    if (!fields) {
      // No fields mean it may only be text
      continue
    }
    const fieldSpec = fields[0]
    const { fieldType, required, options, dependentFields } = fieldSpec

    if (fieldType === 'email') {
      // Email field will be filled automatically. No need to expose to form
      continue
    }

    // Question with text component as question
    const questionType = toFormComponentType(fieldType)
    if (
      questionType !== 'Dropdown' &&
      questionType !== 'RadioTag' &&
      questionType !== 'Input' &&
      questionType !== 'PhoneInput'
    ) {
      continue // Don't know how to show that field or hidden field or just skip in case of email
    }

    const values = options?.map((option) => {
      return {
        key: option.value || null,
        displayLabel: option.label || null,
      }
    })

    const dependents = parseHubspotDependentFields(dependentFields)

    const currentQuestion = {
      name: fieldSpec.name,
      required,
      labelOrQuestion: richText ? removeHtmlTags(richText) : '',
      placeholder: fieldSpec.placeholder || '',
      type: questionType,
      values: values || [],
      dependentFields: dependents,
    }
    questions.push(currentQuestion)
  }

  return questions
}

export function convertFormSpecificationConsentOptions(
  formSpecification: THubspotFormSpecification
): TConsentOptions {
  const legalConsentOptions = formSpecification?.legalConsentOptions ?? {}

  const option = legalConsentOptions.communicationsCheckboxes?.[0] ?? {
    required: false,
    label: '',
    subscriptionTypeId: undefined,
  }

  return {
    description: removeHtmlTags(
      legalConsentOptions?.communicationConsentText ?? ''
    ),
    required: option.required,
    label: removeHtmlTags(option.label),
    subscriptionTypeId: option.subscriptionTypeId,
  }
}

export async function getProfileQuestions() {
  const formSpecification = await getFormSpecification(SIGNUP_FORM_ID)
  return convertFormSpecificationToQuestions(formSpecification)
}

export function convertHubspotToFormSpecification(
  hubspotForm: THubspotFormSpecification
): TFormSpecification {
  const formName = hubspotForm.name

  const formSpec: TFormItemSpecification[] = []

  let previousFormItem = null
  let previousOfPreviousFormItem = null

  for (const fieldGroup of hubspotForm?.fieldGroups) {
    const { richText, fields } = fieldGroup
    if (!fields) {
      formSpec.push({
        type: 'Label',
        labelOrQuestion: removeHtmlTags(richText),
      })
      continue
    }

    const fieldSpec = fields[0]
    const { fieldType, required, options } = fieldSpec
    const questionType = toFormComponentType(fieldType)

    if (questionType === 'unknown') {
      continue // Don't know how to show that field or hidden field or just skip in case of email
    }

    if (
      questionType === 'Input' ||
      questionType === 'EmailInput' ||
      questionType === 'PhoneInput'
    ) {
      formSpec.push({
        type: questionType,
        name: fieldSpec.name,
        labelOrQuestion: removeHtmlTags(richText) || fieldSpec.label,
        required,
      })
      continue
    }

    // List item with text as question/label
    const values = options?.map((option) => {
      return {
        key: option.value,
        displayLabel: option.label,
      }
    })

    if (richText) {
      const currentItem: TFormListInputSpecification = {
        name: fieldSpec.name,
        required,
        labelOrQuestion: removeHtmlTags(richText),
        type: questionType,
        values: values,
      }
      formSpec.push(currentItem)

      if (previousFormItem) {
        previousOfPreviousFormItem = previousFormItem
      }
      previousFormItem = currentItem
    } else {
      // Dependant question, need to get previous item as group and previous of previous as parent
      // First, need to check the name to be the same group of not. If not, just a normal question without question (then just skip it)
      const firstItemInGroup = previousFormItem
      if (!firstItemInGroup) {
        continue
      }
      const parentItem = previousOfPreviousFormItem
      if (!parentItem) {
        continue // In group but no parent question to decide which one can be used. Incorrect so skip
      }
      const currentNameSuffix = fieldSpec.name.split('_').pop()
      const firstGroupNameSuffix = firstItemInGroup.name.split('_').pop()
      if (currentNameSuffix !== firstGroupNameSuffix) {
        continue // Don't know how to group. Also because it doesn't have question text, just skip this incorrect one
      }
      firstItemInGroup.parentFieldName = parentItem.name
      let previousItem = null

      if (firstItemInGroup?.values) {
        // It's a dependant item, so remove values and put value group instead
        previousItem = firstItemInGroup.values
        firstItemInGroup.valueGroup = [
          {
            name: firstItemInGroup.name,
            parentValue: parentItem.values[0]?.key,
            values: previousItem,
          },
        ]
        delete firstItemInGroup.values // Should not put a function because it cannot be serialized
      }

      // Add current answer into the mixed
      const currentIdx = firstItemInGroup.valueGroup.length
      firstItemInGroup.valueGroup.push({
        name: fieldSpec.name,
        parentValue: parentItem.values[currentIdx]?.key,
        values,
      })
    }
  }

  const submitButtonText = hubspotForm.displayOptions?.submitButtonText

  return {
    formName,
    fields: formSpec,
    submitButtonText,
  }
}
